import { Search } from 'lucide-react';
import { useState } from 'react';

import { Button, TabOption, Tabs } from '@/components/chariot-ui';
import { FilterDropdown } from '@/components/filters/FilterDropdown';
import { Table } from '@/components/table/Table';
import { TableControlsContainer } from '@/components/table/TableControlsContainer';
import { ClearFiltersButton } from '@/components/ui/ClearFiltersButton';
import { SearchBar } from '@/components/ui/SearchBar';
import { useGetTechnologyFilters } from '@/hooks/api/useGetTechnologyFilters';
import { Filter, useGetTechnologies } from '@/hooks/useTechnology';
import { OsintConstantineModal } from '@/sections/customerManagement/components/modals/OsintConstantineModal';
import { getFilterDropdownWidth } from '@/sections/technology/config';
import { useTechnologyColumns } from '@/sections/technology/hooks';
import { Vulnerabilities } from '@/sections/vulnerabilities';
import { useGlobalState } from '@/state/global.state';
import { OsintProvider } from '@/state/OsintContext';
import { Technology } from '@/types';
import { capitalize, omit } from '@/utils/lodash.util';

const osintSubTabs: TabOption[] = [
  { label: 'Vulnerabilities', value: 'vulnerabilities' },
  { label: 'Technologies', value: 'technologies' },
];

export function OsintDashboard() {
  const [selectedSubTab, setSelectedSubTab] = useState<TabOption>(
    osintSubTabs[0]
  );
  const [isConstantineModalOpen, setIsConstantineModalOpen] = useState(false);

  return (
    <OsintProvider>
      <div className="flex h-full flex-col">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-chariot-text-primary text-lg font-medium">
              OSINT Research
            </h3>
            <p className="text-chariot-text-secondary text-sm">
              Vulnerability research results from Constantine scans against
              open-source repositories.
            </p>
          </div>
          <Button
            label="Research with Constantine"
            onClick={() => setIsConstantineModalOpen(true)}
            styleType="primary"
            className="rounded-full"
            size="xs"
            startIcon={<Search className="size-4" />}
            data-testid="osint-constantine-button"
          />
        </div>

        <Tabs
          tabs={osintSubTabs}
          selected={selectedSubTab}
          onSelect={setSelectedSubTab}
          variant="primary"
        />

        <div className="mt-4 h-full overflow-y-auto">
          {selectedSubTab.value === 'vulnerabilities' ? (
            <Vulnerabilities
              name="osintDashboardRisks"
              hideTitle
              drawerFilters={['status', 'severity', 'origin']}
            />
          ) : (
            <OsintTechnologies />
          )}
        </div>

        <OsintConstantineModal
          open={isConstantineModalOpen}
          onClose={() => setIsConstantineModalOpen(false)}
        />
      </div>
    </OsintProvider>
  );
}

function OsintTechnologies() {
  const { drawerState } = useGlobalState();
  const { columns } = useTechnologyColumns();
  const [filter, setFilter] = useState<Filter>({});
  const { data: propertyFilters, status: propertyFiltersStatus } =
    useGetTechnologyFilters();
  const {
    data: technologies,
    status,
    error,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useGetTechnologies({ filter });

  return (
    <>
      <TableControlsContainer
        filters={
          <>
            {Object.entries(propertyFilters).map(
              ([key, propertyFilterOptions], index) => (
                <FilterDropdown
                  key={index}
                  id={key}
                  isLoading={propertyFiltersStatus === 'pending'}
                  label={capitalize(key)}
                  options={propertyFilterOptions}
                  selectedValues={filter?.properties?.[key] || []}
                  onApply={selected => {
                    setFilter(prev => ({
                      ...prev,
                      properties:
                        selected.length > 0
                          ? {
                              ...prev?.properties,
                              [key]: selected.map(
                                option => option.value as string
                              ),
                            }
                          : omit(prev?.properties, key),
                    }));
                  }}
                  onClear={() => {
                    setFilter(prev => ({
                      ...prev,
                      properties: omit(prev?.properties, key),
                    }));
                  }}
                  width={getFilterDropdownWidth(key)}
                />
              )
            )}
            {filter?.properties &&
              Object.keys(filter.properties).length > 0 && (
                <ClearFiltersButton
                  onClick={() =>
                    setFilter(prev => ({ ...prev, properties: {} }))
                  }
                />
              )}
          </>
        }
        selectionBar={null}
        filtersRight={
          <SearchBar
            size="sm"
            className="h-fit w-64"
            placeholder="Search technologies"
            value={filter.search}
            onChange={value => setFilter(prev => ({ ...prev, search: value }))}
          />
        }
      />
      <Table<Technology>
        name="osint-dashboard-technologies"
        columns={columns}
        data={technologies}
        error={error}
        status={status}
        onRowClick={technology => {
          drawerState.handleOpenTechnologyDrawer(technology, true);
        }}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        noData={{
          title: 'No OSINT Technologies Yet',
          description:
            'Use the "Research with Constantine" button to add technologies for OSINT research.',
        }}
      />
    </>
  );
}
